import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tag,
  Spin,
  Empty,
  Descriptions,
  Rate,
  Divider,
  List,
  Avatar,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Radio,
  Space,
} from 'antd';
import {
  ClockCircleOutlined,
  TeamOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  RadarChartOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { productService, bookingService, addressService } from '../services/api';
import './CustomerDashboard.css';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const fullAddress = (a) =>
  [a.address_line1, a.address_line2, a.city, a.state, a.country].filter(Boolean).join(', ');

const ServiceDetail = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingModal, setBookingModal] = useState(false);
  const [bookForm] = Form.useForm();
  const [booking, setBooking] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [nearbyProviders, setNearbyProviders] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);

  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  useEffect(() => {
    fetchService();
    fetchQuestions();
  }, [id]);

  useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    loadAddresses();
  }, []);

  const fetchService = async () => {
    setLoading(true);
    try {
      const res = await productService.getProductDetail(id);
      setService(res.data?.data || res.data);
    } catch (err) {
      console.error('Failed to fetch service:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAddresses = async () => {
    try {
      const res = await addressService.getAddresses();
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : [];
      setAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setSelectedAddressId(def.id);
        bookForm.setFieldsValue({ address: fullAddress(def) });
        loadNearbyProviders(def);
      }
    } catch (err) {
      console.error('Failed to load addresses:', err);
    }
  };

  const loadNearbyProviders = async (address) => {
    if (!address?.latitude || !address?.longitude) return;
    setLoadingNearby(true);
    setSelectedProviderId(null);
    try {
      const res = await productService.getNearbyProviders(id, {
        lat: address.latitude,
        lng: address.longitude,
        radius: 15,
      });
      const list = res.data?.data || [];
      setNearbyProviders(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0) {
        setSelectedProviderId(list[0].provider?.id);
      }
    } catch (err) {
      console.error('Failed to load nearby providers:', err);
    } finally {
      setLoadingNearby(false);
    }
  };

  const handleAddressSelect = (addressId) => {
    const addr = addresses.find((a) => a.id === addressId);
    setSelectedAddressId(addressId);
    if (addr) {
      bookForm.setFieldsValue({ address: fullAddress(addr) });
      loadNearbyProviders(addr);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await productService.getServiceQuestions(id);
      setQuestions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      /* the Q&A panel is secondary, so a failure must not break the page */
    }
  };

  const handleAskQuestion = async () => {
    if (!newQuestion.trim()) return;
    setSubmittingQuestion(true);
    try {
      await productService.askServiceQuestion(id, newQuestion.trim());
      message.success('Question submitted!');
      setNewQuestion('');
      fetchQuestions();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleBooking = async (values) => {
    setBooking(true);
    try {
      await bookingService.createBooking({
        product_service_id: +id,
        booking_date: values.date.format('YYYY-MM-DD'),
        booking_time: values.time.format('HH:mm'),
        address: values.address || '',
        address_id: selectedAddressId || undefined,
        provider_id: selectedProviderId || undefined,
        contact_phone: values.contact_phone || '',
        notes: values.notes || '',
      });
      message.success('Booking request submitted!');
      setBookingModal(false);
      bookForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.message || 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  if (!service)
    return (
      <Card>
        <Empty description="Service not found" />
      </Card>
    );

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={16}>
          <Card className="customer-card">
            {service.images?.length > 0 && (
              <img
                src={service.images[0]?.image_url || service.images[0]}
                alt={service.name}
                style={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'cover',
                  borderRadius: 12,
                  marginBottom: 16,
                }}
              />
            )}
            <Title level={3} style={{ color: '#2d5a3d' }}>
              {service.name}
            </Title>
            {service.category && (
              <Tag color="green" style={{ marginBottom: 8 }}>
                {service.category.name || service.category}
              </Tag>
            )}
            <Rate disabled value={service.average_rating || service.rating || 0} allowHalf />
            <Text style={{ marginLeft: 8 }}>
              ({service.reviews_count || service.total_reviews || 0})
            </Text>
            <Divider />
            <Paragraph style={{ color: '#374151', fontSize: 15, lineHeight: 1.8 }}>
              {service.description || service.long_description || 'No description available.'}
            </Paragraph>
            {service.long_description && (
              <Paragraph style={{ color: '#374151', fontSize: 14 }}>
                {service.long_description}
              </Paragraph>
            )}
          </Card>

          <Card
            className="customer-card"
            title={
              <Text strong style={{ color: '#2d5a3d' }}>
                Questions &amp; Answers
              </Text>
            }
            style={{ marginTop: 24 }}
          >
            <Card
              size="small"
              style={{
                background: '#fafff5',
                border: '1px solid #e8f0e8',
                borderRadius: 12,
                marginBottom: 16,
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong style={{ color: '#2d5a3d' }}>
                  Have a question?
                </Text>
                <TextArea
                  rows={2}
                  maxLength={1000}
                  placeholder="Ask a question about this service..."
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
                <Button
                  type="primary"
                  icon={<QuestionCircleOutlined />}
                  loading={submittingQuestion}
                  onClick={handleAskQuestion}
                  disabled={!newQuestion.trim()}
                  style={{
                    borderRadius: 10,
                    background: '#52b788',
                    borderColor: '#52b788',
                    alignSelf: 'flex-end',
                  }}
                >
                  Submit Question
                </Button>
              </Space>
            </Card>
            {questions.length > 0 ? (
              <List
                dataSource={questions}
                renderItem={(q) => (
                  <List.Item style={{ padding: '16px 0' }}>
                    <List.Item.Meta
                      avatar={
                        <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#52b788' }} />
                      }
                      title={
                        <Space>
                          <Text strong style={{ color: '#2d5a3d' }}>
                            {q.customer?.name ||
                              q.customer?.full_name ||
                              q.user?.name ||
                              q.user?.full_name ||
                              'Customer'}
                          </Text>
                          <Text style={{ fontSize: 12, color: '#9ca3af' }}>
                            asked {dayjs(q.created_at).format('MMM D, YYYY')}
                          </Text>
                        </Space>
                      }
                      description={
                        <div>
                          <Paragraph style={{ marginBottom: 8, color: '#4b5563' }}>
                            {q.question || q.comment}
                          </Paragraph>
                          {q.answer ? (
                            <Card
                              size="small"
                              style={{
                                background: '#f0faf0',
                                border: '1px solid #d1fae5',
                                borderRadius: 8,
                                marginTop: 8,
                              }}
                            >
                              <Space>
                                <CheckCircleOutlined style={{ color: '#52b788' }} />
                                <Text strong style={{ color: '#2d5a3d' }}>
                                  Answer:
                                </Text>
                              </Space>
                              <Paragraph style={{ margin: '4px 0 0', color: '#374151' }}>
                                {q.answer}
                              </Paragraph>
                              {q.answeredBy?.name && (
                                <Text style={{ fontSize: 11, color: '#9ca3af' }}>
                                  , {q.answeredBy.name}
                                </Text>
                              )}
                            </Card>
                          ) : (
                            <Tag
                              icon={<ClockCircleOutlined />}
                              color="gold"
                              style={{ fontSize: 12, borderRadius: 6 }}
                            >
                              Awaiting provider response
                            </Tag>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No questions yet. Be the first to ask!" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="customer-card" style={{ position: 'sticky', top: 88 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Title level={3} style={{ color: '#52b788', margin: 0 }}>
                {service.final_price === 0 || service.final_price === '0'
                  ? 'FREE'
                  : `NPR ${service.final_price ?? service.price ?? 0}`}
              </Title>
              {service.final_price !== undefined &&
                service.final_price !== null &&
                Number(service.final_price) !== Number(service.price) && (
                  <Text delete style={{ color: '#9ca3af' }}>
                    NPR {service.price}
                  </Text>
                )}
            </div>
            {service.duration_minutes && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  justifyContent: 'center',
                  marginBottom: 12,
                }}
              >
                <ClockCircleOutlined style={{ color: '#2d5a3d' }} />
                <Text>{service.duration_minutes} minutes</Text>
              </div>
            )}
            <Button
              type="primary"
              block
              size="large"
              icon={<CalendarOutlined />}
              onClick={() => setBookingModal(true)}
              style={{
                background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                border: 'none',
                borderRadius: 10,
                fontWeight: 600,
                height: 48,
              }}
            >
              Book Now
            </Button>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Book Service"
        open={bookingModal}
        onCancel={() => {
          setBookingModal(false);
          bookForm.resetFields();
        }}
        footer={null}
        width={450}
      >
        <Form form={bookForm} layout="vertical" onFinish={handleBooking}>
          <Form.Item
            name="date"
            label="Preferred Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker
              style={{ width: '100%', borderRadius: 10 }}
              disabledDate={(d) => d && d < dayjs().startOf('day')}
            />
          </Form.Item>
          <Form.Item
            name="time"
            label="Preferred Time"
            rules={[{ required: true, message: 'Please select a time' }]}
          >
            <TimePicker
              style={{ width: '100%', borderRadius: 10 }}
              format="HH:mm"
              minuteStep={30}
            />
          </Form.Item>

          {addresses.length > 0 && (
            <Form.Item
              label={
                <span>
                  <EnvironmentOutlined style={{ color: '#52b788' }} /> Saved Address
                </span>
              }
            >
              <Select
                placeholder="Choose a saved address"
                style={{ width: '100%', borderRadius: 10 }}
                value={selectedAddressId}
                onChange={handleAddressSelect}
                options={addresses.map((a) => ({
                  value: a.id,
                  label: `${a.address_type === 'default' ? 'Default · ' : ''}${fullAddress(a)}`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="address"
            label="Service Address"
            rules={[{ required: true, message: 'Please enter your service address' }]}
          >
            <Input.TextArea
              rows={2}
              style={{ borderRadius: 10 }}
              placeholder="Full address where the service is needed"
            />
          </Form.Item>

          {nearbyProviders.length > 0 && (
            <Form.Item
              label={
                <span>
                  <RadarChartOutlined style={{ color: '#52b788' }} /> Recommended Nearby Providers
                </span>
              }
              extra="Providers offering similar services, sorted by distance from your address"
            >
              {loadingNearby ? (
                <Spin size="small" />
              ) : (
                <Radio.Group
                  value={selectedProviderId}
                  onChange={(e) => setSelectedProviderId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {nearbyProviders.map((entry) => {
                      const p = entry.provider || {};
                      const name = p.business_name || p.full_name || p.name || 'Provider';
                      const dist =
                        typeof entry.distance_km === 'number'
                          ? entry.distance_km
                          : parseFloat(entry.distance_km);
                      const mins = entry.estimated_minutes;
                      return (
                        <div key={`${p.id}-${entry.service?.id}`}>
                          <Radio value={p.id}>
                            <span style={{ fontWeight: 600 }}>{name}</span>
                            <span style={{ color: '#6b7280', fontSize: 12 }}>
                              {' '}
                              · {dist ? `${dist.toFixed(1)} km` : ''}
                              {mins ? ` · ~${mins} min away` : ''}
                              {p.is_online ? ' · Online' : ''}
                            </span>
                          </Radio>
                          {entry.is_nearest && (
                            <Tag color="green" style={{ marginLeft: 24, marginTop: 2 }}>
                              Nearest provider
                            </Tag>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                </Radio.Group>
              )}
            </Form.Item>
          )}

          <Form.Item
            name="contact_phone"
            label="Contact Phone"
            rules={[{ required: true, message: 'Please enter a contact phone' }]}
          >
            <Input style={{ borderRadius: 10 }} placeholder="Your phone number" />
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <TextArea
              rows={3}
              style={{ borderRadius: 10 }}
              placeholder="Any special requests or information for the provider..."
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={booking}
            style={{
              background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
              border: 'none',
              borderRadius: 10,
              fontWeight: 600,
              height: 44,
            }}
          >
            Confirm Booking
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceDetail;
